import React, { useEffect, useRef, useState } from 'react';
import ImageGallery from 'react-image-gallery';
import type { GalleryItem } from 'react-image-gallery';
import 'react-image-gallery/build/image-gallery.css';
import PinchZoomPan from 'react-responsive-pinch-zoom-pan';
import loaderInv from '../../assets/images/loader2inv.gif';
import Cancel from '../../assets/images/close_icon@3x.png';
import If from '../If';
import './my-gallery-styles.scss';
import URLs from '../../utils/url';

type IProps = {
  images: string[];
  showNav?: boolean;
  goFullscreen?: boolean;
};

const MyGallery = ({ images, showNav = false, goFullscreen = true }: IProps) => {
  const [data, setData] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [fullScreen, setFullScreen] = useState<boolean>(false);

  const ref = useRef<any>(null);
  const loadedKey = useRef<string>('');

  useEffect(() => {
    const imageList = images.map((item) => {
      const imageStr = `${URLs.assetsBaseUrl}${item}`;
      return {
        original: `${imageStr}${imageStr.indexOf('?') !== -1 ? '&' : '?'}image_size=1200`,
        thumbnail: `${imageStr}${imageStr.indexOf('?') !== -1 ? '&' : '?'}image_size=90`
      };
    });
    // Refetches hand back a new array with the same URLs; the <img> src doesn't change, so
    // no load event fires and the loader would never clear.
    const key = imageList.map((item) => item.original).join('|');
    if (key === loadedKey.current) return;
    loadedKey.current = key;
    setData(imageList);
    if (imageList.length) {
      setLoading(true);
    }
  }, [images]);

  const hideLoader = () => {
    setLoading(false);
  };

  const openFullScreen = () => {
    if (!goFullscreen) return;
    if (!fullScreen) {
      setFullScreen(true);
      ref.current.fullScreen();
    }
  };

  const closeFullScreen = () => {
    setFullScreen(false);
    ref.current.exitFullScreen();
  };

  const renderItem = (item: GalleryItem) => {
    if (fullScreen) {
      return (
        <PinchZoomPan maxScale={3} zoomButtons={fullScreen} position="center">
          <img
            src={item.original}
            className="image-gallery-image"
            alt={item.originalAlt}
            srcSet={item.srcSet}
            sizes={item.sizes}
            onLoad={hideLoader}
            onError={hideLoader}
          />
        </PinchZoomPan>
      );
    }
    // Fullscreen is opened from an explicit target rather than from react-image-gallery's
    // `onClick` prop or the image itself.
    //
    // The library's onClick lands on .image-gallery-slide, a role="button" div at
    // flex: 0 0 100%, so the hit area was the whole gallery column — out to the Ben
    // Bridge wordmark. Moving it to the <img> is closer but still not right: the element
    // hugs the photo, and these product shots carry a wide band of empty white around the
    // ring, so both the cursor and the click still reached well past what looks clickable.
    //
    // The overlay is inset from the photo instead, which is the only way to tighten this
    // without shrinking the ring — narrowing the <img> would make object-fit scale the
    // picture down with it. Cursor and handler live on the same element, so what looks
    // clickable and what is clickable cannot drift apart. Inset is set in the stylesheet.
    //
    // Swipe is unaffected: the touch handlers are on the slide, and these events bubble.
    return (
      <div className="image-gallery-image-wrapper">
        <img
          src={item.original}
          className="image-gallery-image"
          alt={item.originalAlt}
          srcSet={item.srcSet}
          sizes={item.sizes}
          onLoad={hideLoader}
          onError={hideLoader}
        />
        <If condition={goFullscreen}>
          <span className="image-gallery-zoom-target" onClick={openFullScreen} role="presentation" />
        </If>
      </div>
    );
  };

  const renderFullScreenButton = () => (
    <button
      type="button"
      aria-label="Close fullscreen"
      onClick={closeFullScreen}
      className="tw-absolute tw-top-2 tw-right-2 tw-z-10 tw-inline-flex tw-items-center tw-justify-center tw-w-12 tw-h-12 tw-p-0 tw-bg-transparent tw-border-0 tw-cursor-pointer"
    >
      <img src={Cancel} alt="" className="tw-w-9 tw-h-9" />
    </button>
  );

  return (
    <>
      <If condition={loading}>
        <div className="loading-mask" style={{ backgroundImage: `url(${loaderInv})` }} />
      </If>
      <ImageGallery
        ref={ref}
        items={data}
        thumbnailPosition="left"
        showPlayButton={false}
        disableThumbnailScroll
        disableKeyDown
        useBrowserFullscreen={false}
        showFullscreenButton={fullScreen}
        showThumbnails={fullScreen}
        showBullets={!showNav}
        showNav={showNav}
        renderItem={renderItem}
        renderFullscreenButton={renderFullScreenButton}
      />
    </>
  );
};

export default MyGallery;
